'use client';

import { cn } from '@/utils/cn';

export type PillarItem = {
  num: string;
  title: string;
  description: string;
  imageSrc?: string;
};

const DEFAULT_HEADING = 'The Five Pillars of Absolute Trust';

const DEFAULT_SUBHEADING =
  "Powering India's infrastructure requires more than just volume; it requires a relentless commitment to precision and reliability across every facet of our business.";

const DEFAULT_PILLARS: PillarItem[] = [
  {
    num: '01',
    title: 'Safety',
    description:
      "Upholding a 'Zero-Harm' mandate that ensures operational excellence and enduring structural integrity.",
  },
  {
    num: '02',
    title: 'Quality',
    description:
      'Precision-engineered strength and uncompromising consistency in every bag through world-class technology.',
  },
  {
    num: '03',
    title: 'Speed',
    description:
      'Driving national progress with the agility of a 21.5 MTPA powerhouse to meet modern infrastructure demands.',
  },
  {
    num: '04',
    title: 'Commitment',
    description:
      "High-performance solutions and technical integrity required to build India's most ambitious infrastructure.",
  },
  {
    num: '05',
    title: 'Transparency',
    description:
      'Lasting trust through honest partnerships and clear, ethical operations — you always know what we deliver.',
  },
];

export type AboutFivePillarsTrustProps = {
  heading?: string;
  subheading?: string;
  pillars?: PillarItem[];
  className?: string;
};

export default function AboutFivePillarsTrust({
  heading = DEFAULT_HEADING,
  subheading = DEFAULT_SUBHEADING,
  pillars = DEFAULT_PILLARS,
  className,
}: AboutFivePillarsTrustProps) {
  const toDescriptionHtml = (description: string) =>
    description.includes('<') ? description : `<p>${description}</p>`;

  return (
    <section className={cn('bg-[#f3f3f3] px-6 sm:px-10 lg:px-16', className)} aria-label={heading}>
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-8 flex flex-col items-center gap-5 sm:mb-10">
          <div className="flex-1">
            <h2 className="text-3xl font-black leading-tight tracking-tight text-[#111] sm:text-4xl lg:text-5xl">
              {heading}
            </h2>
          </div>
          <p className="max-w-2xl shrink-0 text-[0.82rem] leading-relaxed text-[#111] text-center">
            {subheading}
          </p>
        </div>

        <div
          className="flex items-stretch gap-4 overflow-x-auto py-2 pb-4 lg:overflow-x-visible lg:pb-2"
          style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
        >
          {pillars.map((pillar) => (
            <div
              key={pillar.num}
              className="group relative flex w-[78vw] shrink-0 flex-col self-stretch overflow-hidden rounded-2xl border border-[#111]/8 bg-white shadow-sm transition-all duration-500 ease-out hover:-translate-y-1 hover:border-[#ed1c22] hover:shadow-2xl hover:shadow-[#ed1c22]/10 sm:w-[55vw] lg:w-0 lg:flex-1"
              style={{ scrollSnapAlign: 'start' }}
            >
              <div className="h-[3px] w-full flex-shrink-0 bg-[#ed1c22] transition-all duration-500 ease-out group-hover:h-[5px]" />

              <div className="px-6 pt-6 lg:px-7 lg:pt-7">
                <span className="font-mono text-[0.6rem] font-bold tracking-[0.25em] text-[#111]/20 transition-colors duration-300 group-hover:text-[#ed1c22]">
                  {pillar.num}
                </span>

                <div className="min-h-[5.25rem] sm:min-h-[5.75rem]">
                  <h3 className="mt-3 text-2xl font-black leading-tight tracking-tight text-[#111] transition-colors duration-300 ease-out group-hover:text-[#ed1c22] lg:text-[1.6rem]">
                    {pillar.title}
                  </h3>
                  <div className="mt-3 h-px w-8 bg-[#111]/10 transition-all duration-500 ease-out group-hover:w-full group-hover:bg-[#ed1c22]/30" />
                </div>
              </div>

              <div className="relative flex-1 overflow-hidden pt-4">
                <div className="absolute inset-0 translate-y-full bg-[#ed1c22] transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:translate-y-0" />
                <div className="relative z-[1] px-6 pb-6 pt-4 lg:px-7 lg:pb-7">
                  <div
                    className="text-[0.8rem] font-normal leading-relaxed text-[#111]/50 transition-colors delay-150 duration-300 group-hover:text-white lg:text-[0.85rem] [&_p]:m-0 [&_p]:font-normal [&_p]:leading-relaxed [&_p]:text-inherit [&_p+p]:mt-3 [&_span]:font-normal"
                    dangerouslySetInnerHTML={{ __html: toDescriptionHtml(pillar.description) }}
                  />
                </div>
              </div>

              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-3 -right-2 z-0 select-none text-[5rem] font-black leading-none text-[#111]/[0.035] transition-colors duration-500 group-hover:text-[#ed1c22]/[0.07] lg:text-[6rem]"
              >
                {pillar.num}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
